import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

const CustomerAuthContext = createContext(null);

export const CustomerAuthProvider = ({ children }) => {
  const [me, setMe] = useState(null);         // customer object or null
  const [loading, setLoading] = useState(true); // true while first /me call is in-flight

  const refreshMe = useCallback(async () => {
    // Skip /me during Google OAuth callback — AuthCallback will run first
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await client.get("/auth/me");
      setMe(data);
    } catch (e) {
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const register = async ({ email, password, name }) => {
    const { data } = await client.post("/auth/register", { email, password, name });
    setMe(data);
    return data;
  };

  const login = async ({ email, password }) => {
    const { data } = await client.post("/auth/login", { email, password });
    setMe(data);
    return data;
  };

  const logout = async () => {
    try { await client.post("/auth/logout"); } catch (e) { /* noop */ }
    setMe(null);
  };

  const googleStart = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const completeGoogleSession = async (sessionId) => {
    const { data } = await client.post("/auth/google/session", { session_id: sessionId });
    setMe(data);
    return data;
  };

  const value = useMemo(() => ({
    me, loading, refreshMe, register, login, logout, googleStart, completeGoogleSession, client,
  }), [me, loading, refreshMe]);

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
};

export const useCustomerAuth = () => useContext(CustomerAuthContext);

// Utility for FastAPI 422 detail rendering
export const formatApiError = (detail, fallback = "Something went wrong. Please try again.") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
